import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";

import { useAuth, Roles } from "../../providers/AuthContext";
import {
  createCustomer,
  addCustomerThirdPartySystemMapping,
  deleteCustomerThirdPartySystemMapping,
} from "../../api/Customers";
import {
  postCreateDiscount,
  postAssignDiscountToCustomer,
  deleteDiscount,
} from "../../api/Discounts";
import { getAllThirdPartySystems } from "../../api/Resources";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import DiscountTable from "../../components/customer/DiscountTable";

import CustomerForm from "../../components/customer/CustomerForm";
import CustomerThirdPartyRef from "../../components/customer/CustomerThirdPartyRef";
import { useTranslation } from "react-i18next";

const EditCustomersPage = () => {
  const { t } = useTranslation();
  const { getAccessToken, hasRole } = useAuth();

  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [thirdPartySystemMapping, setThirdPartySystemMapping] = useState([]);
  const [thirdPartySystems, setThirdPartySystems] = useState([]);
  const [addModal, setAddModal] = useState(false);
  const [addDiscountModal, setAddDiscountModal] = useState(false);
  const [addDiscountState, setAddDiscountState] = useState({
    name: "",
    minKwh: "",
    maxKwh: "",
    discountValue: "",
  });
  const [discounts, setDiscounts] = useState([]);
  const [addTPRState, setAddTPRState] = useState({ system: "", id: "" });
  const [confirmState, setConfirmState] = useState({ isOpen: false, id: "" });
  const [loadingCustomer, setLoadingCustomer] = useState(true);
  const [loadingSystems, setLoadingSystems] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setCustomer({ name: "" });
    setLoadingCustomer(false);

    if (hasRole(Roles.Admin)) {
      handleGetAllThirdPartySystems();
    } else {
      setLoadingSystems(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [id]);

  useEffect(() => {
    if (!addModal) {
      setAddTPRState({ system: "", id: "" });
    }
  }, [addModal]);

  const handleGetAllThirdPartySystems = async () => {
    try {
      const accessToken = await getAccessToken();
      const systems = await getAllThirdPartySystems(accessToken);
      setThirdPartySystems(systems);
      setLoadingSystems(false);
    } catch (error) {
      toast.error(t("toast.failedFetch", { entity: t("entity.thirdPartySystems") }), error);
    }
  };

  const handleBackClick = () => {
    if (hasChanges) {
      setConfirmState((s) => ({
        ...s,
        isOpen: true,
        title: t("common.discardChanges"),
        confirm: () => {
          navigate("/customers");
        },
      }));
    } else {
      navigate("/customers");
    }
  };

  const saveAndExit = () => {
    handleCreateCustomer();
  };

  const handleCreateCustomer = async () => {
    const trimmedName = (customer?.name ?? "").trim();
    if (trimmedName.length === 0) {
      toast.error(t("common.nameRequired"));
      return;
    }

    const toastId = toast.loading(t("toast.creating", { entity: t("entity.customer") }));
    try {
      const accessToken = await getAccessToken();

      // Create new customer
      const newCustomer = await createCustomer(customer, accessToken);

      setCustomer(newCustomer);
      if (hasRole(Roles.Admin)) {
          await handleSaveTPRef(newCustomer);
          await handleSaveDiscounts(newCustomer);
      }

      // Successfully created customer and mappings
      toast.success(t("toast.created", { entity: t("entity.customer") }), { id: toastId });
      setHasChanges(false);
      navigate("/customer/" + newCustomer.customerPublicId);
    } catch (error) {
      // Handle errors during any stage of the process
      toast.error(t("toast.failedCreate", { entity: t("entity.customer") }), { id: toastId });
      console.error(error);
    }
  };

  const handleSaveTPRef = async (c) => {
    const accessToken = await getAccessToken();
    // Hantera third-party-system-mappingar
    const thirdPartySystemPromises = thirdPartySystemMapping.map(
      async (thirdPartySystem) => {
        if (thirdPartySystem.isNew) {
          // Lägg till nya third-party system mappingar
          thirdPartySystem.customerPublicId = c.customerPublicId;
          return addCustomerThirdPartySystemMapping(
            thirdPartySystem,
            accessToken
          );
        } else if (thirdPartySystem.isDeleted) {
          // Ta bort befintliga third-party system mappingar
          return deleteCustomerThirdPartySystemMapping(
            thirdPartySystem.thirdPartySystemPublicId,
            thirdPartySystem.customerPublicId,
            thirdPartySystem.thirdPartySystemExternalId,
            accessToken
          );
        }
      }
    );
    // Execute all third-party system mapping changes in parallel
    await Promise.all(thirdPartySystemPromises);
  };

  const handleSaveDiscounts = async (c) => {
    const accessToken = await getAccessToken();

    // Handle discounts
    const discountPromises = discounts.map(async (discount) => {
      if (discount.isNew) {
        // Create new discount
        const discountObj = {
          name: discount.name,
          description: "",
          discountValue: discount.discountValue,
          minKwh: discount.minKwh,
          maxKwh: discount.maxKwh ? discount.maxKwh : null,
          startDateTimeUtc: new Date().toISOString(),
          endDateTimeUtc: null,
          isCalculatedPerChargingSite: true,
          discountConditionType: "Volume",
          discountValueType: "Fixed",
        };

        const newDiscount = await postCreateDiscount(discountObj, accessToken);
        // Assign the new discount to the customer
        await postAssignDiscountToCustomer(
          newDiscount.discountPublicId,
          c.customerPublicId,
          accessToken
        );
      } else if (discount.isDeleted) {
        // Delete existing discount
        await deleteDiscount(discount.discountPublicId, accessToken);
      }
    });

    // Execute all discount changes in parallel
    await Promise.all(discountPromises);
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="absolute inset-0 pointer-events-none">
            <LoadingTablePage
              title={t("entity.customers")}
              loading={loadingCustomer || loadingSystems}
            />
          </div>

          <div
            className={`${
              loadingCustomer || loadingSystems ? "loading" : ""
            } page-content`}
          >
            <div className="flex flex-row items-center pb-4">
              <button onClick={handleBackClick} className="text-black flex ">
                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4 ">
                {t("pages.createCustomer")}
              </h2>
              <div className="flex gap-4 ml-auto">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleBackClick}
                >
                  {t("common.cancel")}
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!customer?.name || !hasChanges}
                  onClick={() => {
                    saveAndExit();
                  }}
                >
                  {t("common.save")}
                </button>
              </div>
            </div>

            <div className="flex flex-col px-4 py-8 shadow-lg rounded-md w-full gap-4">
              <CustomerForm
                customer={customer}
                setCustomer={setCustomer}
                setHasChanges={setHasChanges}
              />

              {hasRole(Roles.Admin) && (
                <div className="flex w-full">
                  <DiscountTable
                    addModal={addDiscountModal}
                    setAddModal={setAddDiscountModal}
                    setAddDiscountState={setAddDiscountState}
                    addDiscountState={addDiscountState}
                    discounts={discounts}
                    setDiscounts={setDiscounts}
                    setConfirmState={setConfirmState}
                    setHasChanges={setHasChanges}
                    setAddDiscountModal={setAddDiscountModal}
                  />
                </div>
              )}

              {hasRole(Roles.Admin) && (
                <div className="flex w-full">
                  <CustomerThirdPartyRef
                    thirdPartySystems={thirdPartySystems}
                    setThirdPartySystems={setThirdPartySystems}
                    thirdPartySystemMapping={thirdPartySystemMapping}
                    setThirdPartySystemMapping={setThirdPartySystemMapping}
                    addModal={addModal}
                    setAddModal={setAddModal}
                    addTPRState={addTPRState}
                    setAddTPRState={setAddTPRState}
                    confirmState={confirmState}
                    setConfirmState={setConfirmState}
                    setHasChanges={setHasChanges}
                  />
                </div>
              )}
            </div>
          </div>
        </main>
        <ConfirmationModal
          isOpen={confirmState.isOpen}
          title={
            confirmState.title
              ? confirmState.title
              : t("confirm.deleteThirdPartyRef")
          }
          onClose={() => {
            setConfirmState((s) => ({ ...s, isOpen: false }));
          }}
          onConfirm={() => {
            setConfirmState((s) => ({ ...s, isOpen: false }));
            confirmState.confirm();
          }}
        />
      </div>
    </>
  );
};

export default EditCustomersPage;
